import { Icon } from '@iconify/react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { UserStatus, type User } from '../../../types/ApiTypes'
import BlockUserModal from '../../components/BlockUserModal'
import ConfirmPinModal from '../../components/ConfirmPinModal'
import Pagination from '../../components/Pagination'
import ReviewKYCModal from '../../components/ReviewKYCModal'
import SendMoneySidebar from '../../components/SendMoneySidebar'
import SkeletonTable from '../../components/SkeletonTable'
import UserActionMenu from '../../components/UserActionMenu'
import { useApi } from '../../hooks/useApi'
import Dashboard from '../../layout/Dashboard'
import { Content, Heading } from '../../styles/OverviewStyle'
import { Badge, MonoSmall, MonoUsername, TableHeadRow, TableRow, TableWrap } from '../../styles/TableStyle'
import { formatDate } from '../../utils/dateHelpers'

interface UserRow {
   id: string
   fullName: string
   username: string
   email: string
   phone: string
   status: 'Verified' | 'Unverified' | 'Blocked'
   joined: string
}

function RowActionCell({ user, onReviewKYC, onBlockUser }: { user: User; onReviewKYC: () => void; onBlockUser: () => void }): React.JSX.Element {
   const btnRef = useRef<HTMLButtonElement | null>(null)
   const [open, setOpen] = useState(false)

   return (
      <div className='relative flex justify-end pr-1'>
         <button
            ref={btnRef}
            onClick={() => setOpen(o => !o)}
            className='p-1.5 h-[26px] text-[12px] rounded-md hover:bg-[#e1e1e1] transition-colors duration-300 flex items-center cursor-pointer'
            aria-haspopup='menu'
            aria-expanded={open}
            aria-label='Open user actions'
         >
            <Icon icon='solar:menu-dots-bold' width={22} className='text-[#6D6D72]' />
         </button>
         <UserActionMenu
            open={open}
            onClose={() => setOpen(false)}
            anchorRef={btnRef}
            user={user}
            onReviewKYC={onReviewKYC}
            onBlockUser={onBlockUser}
         />
      </div>
   )
}

export default function UserManagement(): React.JSX.Element {
   const [query, setQuery] = useState('')
   const [debouncedQuery, setDebouncedQuery] = useState('')
   const [sendOpen, setSendOpen] = useState(false)
   const [selectedUser, setSelectedUser] = useState<string | null>(null)
   const [blockOpen, setBlockOpen] = useState(false)
   const [pinOpen, setPinOpen] = useState(false)
   const [kycOpen, setKycOpen] = useState(false)
   const { user: userService } = useApi()
   const [users, setUsers] = useState<User[]>([])
   const [loading, setLoading] = useState(true)
   const [currentPage, setCurrentPage] = useState(1)
   const [totalPages, setTotalPages] = useState(0)
   const pageSize = 20

   useEffect(() => {
      const timer = setTimeout(() => setDebouncedQuery(query), 500)
      return () => clearTimeout(timer)
   }, [query])

   useEffect(() => {
      const loadUsers = async () => {
         setLoading(true)
         const params: { pageNumber: number; pageSize: number; search?: string } = { pageNumber: currentPage, pageSize }
         if (debouncedQuery) params.search = debouncedQuery
         const res = await userService.getUsers(params)
         if (res.status === 'success' && res.data) {
            setUsers(res.data.users)
            setTotalPages(res.data.totalPages)
         }
         setLoading(false)
      }
      loadUsers()
   }, [userService, currentPage, debouncedQuery])

   const mapStatus = (status: UserStatus): 'Verified' | 'Unverified' | 'Blocked' => {
      if (status === UserStatus.VERIFIED) return 'Verified'
      if (status === UserStatus.BLOCKED) return 'Blocked'
      return 'Unverified'
   }

   const rows: UserRow[] = users.map(u => ({
      id: u.id,
      fullName: u.fullName,
      username: `@${u.username ?? 'N/A'}`,
      email: u.email,
      phone: u.phone,
      status: mapStatus(u.status),
      joined: formatDate(u.registrationDate)
   }))



   const gridCols = '220px 180px 1fr 180px 120px 200px 60px'

   return (
      <Dashboard title="User Management / All" icon="solar:user-circle-linear" active="user_management">
         <Content>
            <div className="flex items-center justify-between">
               <Heading>User Management</Heading>
               <button onClick={() => setSendOpen(true)} className="px-4 py-2 rounded-full bg-[#AF7AD5] text-white text-sm font-semibold tracking-tight hover:bg-[#9b63c9] cursor-pointer">
                  Send Money
               </button>
            </div>

            <div className="flex items-center gap-4 mt-6 mb-4">
               <div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
                  <Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
                  <input
                     value={query}
                     onChange={e => setQuery(e.target.value)}
                     placeholder="Search"
                     className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
               </div>

               <button
                  className="ml-auto flex items-center gap-1.5 px-3 py-1.5 h-[28px] text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors"
               >
                  <span className='text-neutral-900 font-medium'>Export csv</span>
                  <Icon icon='solar:upload-linear' width={14} />
               </button>
            </div>

            {loading ? <SkeletonTable /> : (
               <TableWrap>
                  <TableHeadRow style={{ gridTemplateColumns: gridCols }}>
                     <div>Full name</div>
                     <div>Username</div>
                     <div>Email</div>
                     <div>Phone</div>
                     <div>Status</div>
                     <div>Joined</div>
                     <div></div>
                  </TableHeadRow>
                  {rows.map((r) => {
                     const user = users.find(u => u.id === r.id)!
                     return (
                        <TableRow key={r.id} style={{ gridTemplateColumns: gridCols }} className="hover:bg-[#FAFAFB] transition-colors">
                           <div className='text-sm text-[#101018]'>{r.fullName}</div>
                           <MonoUsername>{r.username}</MonoUsername>
                           <div className='text-sm text-[#6D6D72] truncate'>{r.email}</div>
                           <div className='text-sm text-[#6D6D72]'>{r.phone}</div>
                           <div>
                              <Badge variant={r.status === 'Verified' ? 'success' : r.status === 'Blocked' ? 'failed' : 'pending'}>{r.status}</Badge>
                           </div>
                           <MonoSmall>{r.joined}</MonoSmall>
                           <RowActionCell
                              user={user}
                              onReviewKYC={() => { setSelectedUser(user.id); setKycOpen(true) }}
                              onBlockUser={() => { setSelectedUser(user.id); setBlockOpen(true) }}
                           />
                        </TableRow>
                     )
                  })}
               </TableWrap>
            )}

            {!loading && totalPages > 0 && (
               <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            )}

            <SendMoneySidebar open={sendOpen} onClose={() => setSendOpen(false)} />

            {/* Modals triggered from row action menu */}
            <BlockUserModal
               open={blockOpen}
               username={users.find(u => u.id === selectedUser)?.username ?? ''}
               isBlocked={users.find(u => u.id === selectedUser)?.status === UserStatus.BLOCKED}
               onClose={() => setBlockOpen(false)}
               onConfirm={async () => {
                  setBlockOpen(false)
                  try {
                     const res = await userService.blockUser(selectedUser!)
                     if (res.status === 'success') {
                        setUsers(prev => prev.map(u => u.id === selectedUser ? { ...u, status: u.status === UserStatus.BLOCKED ? UserStatus.VERIFIED : UserStatus.BLOCKED } : u))
                     }
                  } catch (error) {
                     toast.error(error instanceof Error ? error.message : 'An error occurred')
                  }
               }}
            />
            <ConfirmPinModal
               open={pinOpen}
               onClose={() => setPinOpen(false)}
               onConfirm={() => setPinOpen(false)}
            />
            <ReviewKYCModal
               open={kycOpen}
               username={users.find(u => u.id === selectedUser)?.username ?? ''}
               bvnNumber={users.find(u => u.id === selectedUser)?.bvnNumber}
               onClose={() => setKycOpen(false)}
            />
         </Content>
      </Dashboard>
   )
}
